/* eslint-disable camelcase */
import { Avatar, Icon, message, theme } from 'components';
import { ContextualChatContext } from 'contexts';
import { useDebounce } from 'hooks';
import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import fileStackClient from '@app/filestack';
import { useMutation } from '@tanstack/react-query';
import { Loading } from '@app/react-common';

interface IStreamAsset {
  type: string;
  file_size: number;
  mime_type: string;
  title: string;
  asset_url?: string;
  image_url?: string;
}

const CustomChannelInput = () => {
  const {
    channelText,
    channelAttachments,
    setChannelAttachments,
    setChannelText,
    channel,
    quotedMessage,
    setQuotedMessage,
  } = useContext(ContextualChatContext);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localText, setLocalText] = useState(channelText);
  const debouncedText = useDebounce(localText, 250);

  useEffect(() => {
    setChannelText(debouncedText);
  }, [debouncedText, setChannelText]);

  const { mutateAsync: sendMessageAsync, isPending: isLoading } = useMutation({
    mutationFn: async () => {
      const uploadedAttachments = await Promise.all(
        channelAttachments.map(async file => {
          const response = await fileStackClient.upload(file);

          const type = file.type.startsWith('image/') ? 'image' : 'file';
          const indexKey = type === 'file' ? 'asset_url' : 'image_url';

          const asset: IStreamAsset = {
            type,
            file_size: file.size,
            mime_type: file.type,
            title: file.name,
          };

          asset[indexKey] = response.url;

          return asset;
        }),
      );

      return channel.sendMessage({
        text: localText,
        attachments: uploadedAttachments,
        quoted_message_id: quotedMessage?.id,
      });
    },
    onSuccess: () => {
      if (textareaRef.current) {
        textareaRef.current.value = '';
      }
      setChannelText('');
      setLocalText('');
      setChannelAttachments([]);
      handleInput(true);
      setQuotedMessage(undefined);
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 10);
    },
    onError: () => {
      message.error('Unable to send message, please try again');
    },
  });

  const handleAttachmentClick = () => {
    if (isLoading) {
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = (e: any) => {
      // TODO: future, restrict file types and show message.error() saying we do not accept these files types
      const files = Array.from(e.target.files);
      setChannelAttachments(prev => [...prev, ...files] as File[]);
    };
    input.click();
  };

  const handleInput = (reset?: boolean) => {
    const el = textareaRef.current;
    if (!el) return;

    if (reset && !el.value) {
      el.style.height = '1.5em';
      return;
    }

    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setChannelAttachments(prev => [...prev, ...files] as File[]);
    }
  };

  const replyMessageContent = useMemo((): ReactNode => {
    if (!quotedMessage) {
      return null;
    }

    const getInternalContent = () => {
      if (quotedMessage?.text || !quotedMessage?.attachments?.length) {
        return <span>{quotedMessage?.text || ' '}</span>;
      }

      const file = quotedMessage?.attachments?.at(0);

      if (!file) {
        return null;
      }

      return file.type === 'image' ? (
        <img src={file?.image_url} alt="reply-image" />
      ) : (
        <span>{file?.title ?? 'Attachment'}</span>
      );
    };

    const nameParts = quotedMessage?.user?.name?.split(' ');
    const userName =
      `${nameParts?.at(0) || ''} ${nameParts?.at(1) || ''}`.trim();

    return (
      <ReplyContainer>
        <Avatar
          user={{
            firstName: nameParts?.at(0) || '',
            lastName: nameParts?.at(1) || '',
          }}
          width={32}
        />
        <StyledReplyBubb>
          <ReplyHeader>Reply to {userName}</ReplyHeader>
          {getInternalContent()}
          <RemoveButton onClick={() => setQuotedMessage(undefined)}>
            <Icon type="antdCloseOutlined" />
          </RemoveButton>
        </StyledReplyBubb>
      </ReplyContainer>
    );
  }, [quotedMessage, setQuotedMessage]);

  const showSendButton =
    (localText && localText.trim()?.length > 0) ||
    (channelAttachments && channelAttachments?.length > 0);

  return (
    <Container>
      <AttachmentButton onClick={handleAttachmentClick}>
        <Icon type="plus" width={20} color="primary" />
      </AttachmentButton>
      <InputArea
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={isDragging ? 'dragging' : ''}
      >
        {replyMessageContent}
        {channelAttachments?.length > 0 && (
          <AttachmentPreview>
            {channelAttachments.map((file, idx) => (
              <AttachmentItem key={`attachment-${idx}`}>
                {file.type.startsWith('image/') ? (
                  <img src={URL.createObjectURL(file)} alt={file.name} />
                ) : (
                  <span>{file.name}</span>
                )}
                {!isLoading && (
                  <RemoveButton
                    onClick={() =>
                      setChannelAttachments(prev =>
                        prev.filter((_, i) => i !== idx),
                      )
                    }
                  >
                    <Icon type="antdCloseCircleOutlined" />
                  </RemoveButton>
                )}
              </AttachmentItem>
            ))}
          </AttachmentPreview>
        )}
        <InputRow showSend={showSendButton}>
          <SearchInput
            ref={textareaRef}
            onFocus={() => handleInput()}
            disabled={isLoading}
            onBlur={() => handleInput(true)}
            placeholder="Message..."
            defaultValue={channelText}
            onChange={e => setLocalText(e.target.value)}
            onKeyDown={async e => {
              // Allow for new lines with Shift + Enter
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (showSendButton && !isLoading) {
                  await sendMessageAsync();
                }
              }
            }}
          />

          <SendButton
            onClick={() => {
              if (showSendButton && !isLoading) {
                sendMessageAsync();
              }
            }}
            disabled={!showSendButton || isLoading}
          >
            {isLoading ? (
              <Loading size="fa-sm" noPadding />
            ) : (
              <Icon type="antdArrowRightOutlined" width={15} color="white" />
            )}
          </SendButton>
        </InputRow>
      </InputArea>
    </Container>
  );
};

const StyledReplyBubb = styled.div`
  border-radius: 12px;
  border-bottom-left-radius: 0px;
  background-color: #f5f5f5;
  border-left: 3px solid #2c6ef2;
  margin-left: 8px;
  margin-bottom: 8px;
  padding: 8px 12px;
  padding-right: 30px;
  max-width: 300px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;

  span {
    font-size: 13px;
    color: #666;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  img {
    border-radius: 6px;
    max-width: 50px;
    max-height: 50px;
    object-fit: cover;
  }
`;

const ReplyHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #2c6ef2;
  margin-bottom: 2px;
`;

const ReplyContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  cursor: pointer;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const InputArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid #c5c6d0;
  border-radius: 8px;
  padding: 7px 16px;
  padding-right: 5px;
  width: calc(100% - 100px);

  &.dragging {
    border: 2px dashed ${theme.getColor('primary')};
    background-color: #f0f7ff;
  }

  &:focus-within {
    box-shadow: 0 0 0 2px ${theme.getColor('primary')};
    border: 1px solid transparent;
  }
`;

const SearchInput = styled.textarea`
  flex: 1;
  font-size: 16px;
  color: #333;
  background-color: transparent;
  border: none;
  outline: none;
  resize: none;
  height: 1.5em;
  min-height: 1.5em;
  max-height: 3em;
  overflow-y: auto;
  line-height: 1.5em;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;

  scrollbar-width: none;
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  width: 100%;
  max-width: 100%;
`;

const AttachmentButton = styled.div`
  background-color: #f0f7ff;
  display: flex;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
`;

const SendButton = styled.button<{ disabled?: boolean }>`
  background-color: ${({ disabled }) => (disabled ? '#ccc' : '#2c6ef2')};
  display: flex;
  padding: 6px;
  border-radius: 50%;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transform: rotate(-90deg);
  width: 27px;
  height: 27px;
  margin-top: auto;
  border: none;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  align-items: center;
`;

const InputRow = styled.div<{ showSend: boolean }>`
  display: flex;
  flex: 1;
  gap: 5px;

  ${SendButton} {
    visibility: ${({ showSend }) => (showSend ? 'visible' : 'hidden')};
  }

  &:focus-within ${SendButton} {
    visibility: visible;
  }
`;

const AttachmentPreview = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  padding-top: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  flex: 0 0 auto;
`;

const AttachmentItem = styled.div`
  position: relative;
  background-color: #e8e8e8;
  border-radius: 8px;
  font-size: 12px;
  width: 70px;
  height: 70px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #c5c6d0;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: cover;
    border-radius: 4px;
  }

  span {
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export default CustomChannelInput;
